/**
 * Dead links — published URLs people still follow that go nowhere: a link we
 * retired (or whose window ended), or a slug that never existed, usually a typo
 * in a caption. Each follow landed the person on the site's home page; this is
 * the list of which URLs are still out there doing it.
 *
 * From the redirector's own record (`link_dead` acts), so it is people, not
 * automation. See backend `admin/content/deadLinks.ts`.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, DataTable, SearchFilterBar, type ColumnDef, type FilterDef, type SortState } from '@shared-ui/components';
import { deadLinksApi, type DeadLink } from '../api/publishedLinksApi';

const PERIODS = [7, 30, 90, 180];

/** The period as the page's own filter bar spells a filter — required, so
 *  there is always a window and never an "any" option. */
const PERIOD_FILTER: FilterDef[] = [{
  key: 'days', kind: 'select', placeholder: 'Period', required: true,
  options: PERIODS.map(d => ({ value: String(d), label: `Last ${d} days` })),
}];

const STATUS: Record<DeadLink['status'], { label: string; variant: 'default' | 'warning' | 'error' | 'success'; tip: string }> = {
  retired: { label: 'Retired', variant: 'default', tip: 'A link we retired. Open it to restore it, if it is still worth following.' },
  ended: { label: 'Window ended', variant: 'warning', tip: 'A link whose start or end date put it outside its window.' },
  unknown: { label: 'Never existed', variant: 'error', tip: 'No link has this slug — usually a typo in a caption or a description.' },
  active: { label: 'Live now', variant: 'success', tip: 'Dead when it was followed; restored or opened since.' },
};

/** `09/25 13:40` — month/day, local, 24-hour; the same compact form as Site
 *  Usage's Landed column. */
function fmtWhen(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function DeadLinks() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Record<string, string>>({ days: '30' });
  const days = Number(filters.days) || 30;
  const [sort, setSort] = useState<SortState>({ sortBy: 'follows', sortOrder: 'desc' });
  const { data, isLoading, error } = useQuery({ queryKey: ['dead-links', days], queryFn: () => deadLinksApi.list(days) });

  const opens = (l: DeadLink) => l.status !== 'unknown';
  const columns: ColumnDef<DeadLink>[] = [
    { key: 'slug', header: 'Link', sortable: true, sortValue: l => l.slug, mobile: 'title',
      render: l => <span className="pl-slug">/p/{l.slug}</span> },
    { key: 'status', header: 'Status', sortable: true, sortValue: l => l.status, mobile: 'accessory',
      render: l => <span title={STATUS[l.status].tip}><Badge variant={STATUS[l.status].variant} size="sm">{STATUS[l.status].label}</Badge></span> },
    { key: 'follows', header: 'Follows', sortable: true, className: 'pl-num', sortValue: l => l.follows,
      render: l => l.follows.toLocaleString() },
    { key: 'last', header: 'Last followed', sortable: true, sortValue: l => l.lastFollowedAt,
      render: l => <span title={l.lastFollowedAt}>{fmtWhen(l.lastFollowedAt)}</span> },
    { key: 'referrer', header: 'Top referrer', sortable: true, sortValue: l => l.topReferrer ?? '',
      render: l => l.topReferrer ?? <span className="pl-sub">—</span> },
  ];

  return (
    <section className="pl-dead">
      {/* A section of the page, not a second page: a rule above, a heading
          that never wraps, and what it lists said in a line under it rather
          than hidden behind an icon. */}
      <header className="pl-dead-head">
        <h2>Dead links</h2>
        <p className="pl-sub">
          Published URLs people followed that went nowhere — each follow landed on the site’s home page. A retired
          link opens its page, where it can be restored; one that never existed is usually a typo where it was
          posted. Automation is not counted.
        </p>
      </header>
      <SearchFilterBar filters={PERIOD_FILTER} values={filters} onChange={setFilters} hideSearch hideFetch />
      {error
        ? <p className="pl-error">{(error as Error).message}</p>
        : (
          <DataTable<DeadLink>
            columns={columns}
            data={data?.links ?? []}
            rowKey={l => l.slug}
            sort={sort}
            onSortChange={setSort}
            loading={isLoading}
            onRowClick={l => { if (opens(l)) navigate(`/published-links/${l.slug}`); }}
            rowClickable={opens}
            mobileCards
            compact
            emptyMessage={`Nobody followed a dead link in the last ${days} days.`}
          />
        )}
    </section>
  );
}
